import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CreditCard, Gift, Zap } from 'lucide-react';

const PricingPreviewSection = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="pricing" ref={ref} className="py-24 bg-[#FAF3E0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Transparente Monatspreise
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Wählen Sie das Paket, das zu Ihrem Geschäft passt. 
            Jederzeit kündbar, Credits verfallen nie.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Starter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
              <Gift className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">39,99€</span>
              <span className="text-gray-600 ml-2">/Monat</span>
            </div>
            <p className="text-gray-600 mb-2">Ideal für Einzelunternehmer</p>
            <div className="text-sm text-gray-500 mb-6">
              50 Credits/Monat = 50 Kontakte
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                </div>
                <span className="text-gray-700">50 Kontakte pro Monat</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                </div>
                <span className="text-gray-700">Alle Filter verfügbar</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                </div>
                <span className="text-gray-700">CSV-Export</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                </div>
                <span className="text-gray-700">Monatlich kündbar</span>
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/app')} 
              className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 rounded-xl"
            >
              Jetzt starten
            </Button>
          </motion.div>

          {/* Professional - Featured */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-gradient-to-br from-[#2835F7] to-[#1a25c9] rounded-2xl p-8 shadow-2xl border-2 border-[#2835F7] relative hover:scale-105 transition-all duration-300"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
              AM BELIEBTESTEN
            </div>
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-lg">
              <Zap className="w-7 h-7 text-[#2835F7]" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-white">99,99€</span>
              <span className="text-white/90 ml-2">/Monat</span>
            </div>
            <p className="text-white/90 mb-2">Perfekt für kleine Teams</p>
            <div className="text-sm text-white/80 mb-6">
              200 Credits/Monat = 200 Kontakte
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                <span className="text-white">200 Kontakte pro Monat</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                <span className="text-white">Unbegrenzte Exporte</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                <span className="text-white">Priority Support</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                <span className="text-white">Monatlich kündbar</span>
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/app')} 
              className="w-full bg-white hover:bg-gray-100 text-[#2835F7] font-bold py-3 rounded-xl"
            >
              Jetzt abonnieren
            </Button>
          </motion.div>

          {/* Business */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
              <CreditCard className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Business</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">249,99€</span>
              <span className="text-gray-600 ml-2">/Monat</span>
            </div>
            <p className="text-gray-600 mb-2">Für wachsende Unternehmen</p>
            <div className="text-sm text-gray-500 mb-6">
              750 Credits/Monat = 750 Kontakte
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full" />
                </div>
                <span className="text-gray-700">750 Kontakte pro Monat</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full" />
                </div>
                <span className="text-gray-700">Unbegrenzte Exporte</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full" />
                </div>
                <span className="text-gray-700">VIP Support</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-purple-600 rounded-full" />
                </div>
                <span className="text-gray-700">Monatlich kündbar</span>
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/app')} 
              variant="outline" 
              className="w-full border-2 border-gray-900 hover:bg-gray-900 hover:text-white font-bold py-3 rounded-xl"
            >
              Jetzt abonnieren
            </Button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center bg-white rounded-2xl p-8 shadow-lg max-w-3xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Flexible Abrechnung
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Jedes Monatsabo beinhaltet eine feste Anzahl an Credits. 
            Nicht genutzte Credits verfallen nicht und können im nächsten Monat verwendet werden.
            Sie zahlen nur für Ihr Abo - keine versteckten Kosten, keine Überraschungen.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingPreviewSection;